<template>
  <div class="consistency-workbench">
    <div class="consistency-workbench__header">
      <div>
        <strong>一致性资产设定</strong>
        <p>基于当前分镜自动抽取角色、场景和道具候选，先确认官方参考，再进入单镜图片和视频生成。</p>
      </div>
      <div class="result-actions">
        <el-button plain @click="emit('add-asset', consistencyAssetTab)">新增{{ CONSISTENCY_ASSET_LABELS[consistencyAssetTab] }}</el-button>
        <el-button type="primary" :loading="consistencySaving" @click="emit('save-assets')">保存一致性资产</el-button>
      </div>
    </div>

    <div class="consistency-overview">
      <article v-for="item in consistencyAssetStats" :key="item.key" class="consistency-overview__card">
        <span>{{ item.label }}</span>
        <strong>{{ item.ready }}/{{ item.total }}</strong>
        <small>{{ item.total ? '已准备官方参考图' : '等待从分镜抽取或手动新增' }}</small>
      </article>
    </div>

    <div class="workspace-switcher workspace-switcher--compact">
      <button
        v-for="option in CONSISTENCY_ASSET_TAB_OPTIONS"
        :key="`consistency-${option.value}`"
        type="button"
        class="workspace-switcher__tab"
        :class="{ 'workspace-switcher__tab--active': consistencyAssetTab === option.value }"
        @click="emit('update:consistencyAssetTab', option.value)"
      >
        {{ option.label }}
      </button>
    </div>

    <div class="consistency-workbench__hint">
      素材中心负责统一存储与复用，一致性资产页负责当前项目的官方定版；无论选图、上传还是 AI 生成，都会回到素材中心沉淀，再绑定为这里的官方参考。
    </div>

    <div v-if="activeConsistencyAssets.length" class="consistency-grid">
      <article v-for="asset in activeConsistencyAssets" :key="asset.id" class="consistency-card">
        <div class="consistency-card__header">
          <div>
            <span class="consistency-card__eyebrow">{{ CONSISTENCY_ASSET_LABELS[asset.type] }}官方参考</span>
            <strong>{{ asset.name || '未命名资产' }}</strong>
          </div>
          <div class="consistency-card__meta">
            <span v-if="asset.linked_scene_numbers.length">场景 {{ asset.linked_scene_numbers.join('、') }}</span>
            <span>{{ asset.image_url ? formatSceneImageSource(asset.selected_image_source || '') : '待准备参考图' }}</span>
            <span v-if="asset.material_id">已归档素材中心</span>
          </div>
        </div>

        <div class="consistency-card__body">
          <div class="consistency-card__preview">
            <img v-if="asset.image_url" :src="asset.image_url" :alt="asset.name || CONSISTENCY_ASSET_LABELS[asset.type]" />
            <div v-else class="consistency-card__preview-placeholder">
              <strong>{{ CONSISTENCY_ASSET_LABELS[asset.type] }}参考图待补充</strong>
              <p>可直接生成，也可上传人工确认过的官方参考图。</p>
            </div>
          </div>

          <div class="consistency-card__fields">
            <el-input v-model="asset.name" maxlength="80" placeholder="填写官方名称" @input="emit('asset-input')" />
            <ReferenceLinkActions v-if="asset.image_url" :url="asset.image_url" compact />
            <PromptAssistTextarea
              v-model="asset.prompt"
              field-label="一致性资产提示词"
              assistant-title="一致性设定助手"
              assistant-description="你可以直接补充希望稳定下来的服装、材质、颜色、气质或禁变项，系统会把它整理成更适合后续复用的资产设定文本。"
              :project-id="projectId"
              :asset-type="asset.type"
              :asset-name="asset.name || CONSISTENCY_ASSET_LABELS[asset.type]"
              purpose="consistency_prompt"
              :rows="4"
              :maxlength="1000"
              :placeholder="getConsistencyPromptPlaceholder(asset.type, asset.name)"
              @input="emit('asset-input')"
            />
          </div>
        </div>

        <div class="consistency-card__actions">
          <el-button plain @click="emit('open-knowledge-base', { type: asset.type, id: asset.id })">
            从素材中心选图
          </el-button>
          <el-button
            plain
            :loading="consistencyAssetImageGenerating[buildConsistencyAssetLoadingKey(asset.type, asset.id)]"
            @click="emit('generate-reference', { type: asset.type, id: asset.id })"
          >
            生成参考图
          </el-button>
          <el-button
            :loading="consistencyAssetImageUploading[buildConsistencyAssetLoadingKey(asset.type, asset.id)]"
            @click="emit('upload-asset', { type: asset.type, id: asset.id })"
          >
            上传图片
          </el-button>
          <el-button v-if="asset.image_url" type="danger" plain @click="emit('clear-image', { type: asset.type, id: asset.id })">
            清空图片
          </el-button>
          <el-button link type="danger" @click="emit('remove-asset', { type: asset.type, id: asset.id })">
            删除资产
          </el-button>
        </div>
      </article>
    </div>

    <el-empty v-else description="当前还没有从分镜里抽取到一致性资产，可先回到分镜剧本补充角色、场景和道具，再回来确认。">
      <el-button type="primary" plain @click="emit('add-asset', consistencyAssetTab)">手动新增{{ CONSISTENCY_ASSET_LABELS[consistencyAssetTab] }}</el-button>
    </el-empty>
  </div>
</template>

<script setup lang="ts">
import PromptAssistTextarea from '@/components/PromptAssistTextarea.vue'
import ReferenceLinkActions from '@/components/ReferenceLinkActions.vue'
import {
  buildConsistencyAssetLoadingKey,
  CONSISTENCY_ASSET_LABELS,
  CONSISTENCY_ASSET_TAB_OPTIONS,
  getConsistencyPromptPlaceholder,
} from './consistencyAssets'
import type { ConsistencyAssetDraft, ConsistencyAssetType } from './types'

defineProps<{
  projectId: number
  consistencySaving: boolean
  consistencyAssetTab: ConsistencyAssetType
  consistencyAssetStats: Array<{ key: string; label: string; total: number; ready: number }>
  activeConsistencyAssets: ConsistencyAssetDraft[]
  consistencyAssetImageGenerating: Record<string, boolean>
  consistencyAssetImageUploading: Record<string, boolean>
  formatSceneImageSource: (source: string) => string
}>()

const emit = defineEmits<{
  (event: 'update:consistencyAssetTab', value: ConsistencyAssetType): void
  (event: 'add-asset', value: ConsistencyAssetType): void
  (event: 'save-assets'): void
  (event: 'asset-input'): void
  (event: 'open-knowledge-base', payload: { type: ConsistencyAssetType; id: string }): void
  (event: 'generate-reference', payload: { type: ConsistencyAssetType; id: string }): void
  (event: 'upload-asset', payload: { type: ConsistencyAssetType; id: string }): void
  (event: 'clear-image', payload: { type: ConsistencyAssetType; id: string }): void
  (event: 'remove-asset', payload: { type: ConsistencyAssetType; id: string }): void
}>()
</script>

<style scoped>
.workspace-switcher {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: max-content;
  max-width: 100%;
  padding: 10px;
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.95), rgba(246, 250, 255, 0.98));
  border: 1px solid rgba(191, 219, 254, 0.58);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9), 0 16px 40px rgba(15, 23, 42, 0.07);
}

.workspace-switcher::before {
  content: '';
  position: absolute;
  inset: 8px;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(226, 232, 240, 0.3), rgba(240, 249, 255, 0.2));
  pointer-events: none;
}

.workspace-switcher--compact {
  width: fit-content;
}

.workspace-switcher__tab {
  position: relative;
  z-index: 1;
  border: 0;
  min-height: 46px;
  padding: 0 22px;
  border-radius: 999px;
  background: transparent;
  color: #475569;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s ease;
}

.workspace-switcher__tab:hover {
  color: #10243d;
  background: rgba(255, 255, 255, 0.6);
}

.workspace-switcher__tab--active {
  background: linear-gradient(135deg, #12c2a2, #0ea5a4);
  color: #f8fffd;
  box-shadow: 0 12px 28px rgba(20, 184, 166, 0.24);
}

.consistency-workbench {
  display: grid;
  gap: 18px;
}

.consistency-workbench__header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.consistency-workbench__header strong {
  display: block;
  color: #10243d;
  font-size: 20px;
}

.consistency-workbench__header p,
.consistency-workbench__hint {
  margin: 8px 0 0;
  color: #64748b;
  line-height: 1.75;
}

.consistency-workbench__hint {
  padding: 16px 18px;
  border-radius: 18px;
  background: rgba(240, 249, 255, 0.82);
  border: 1px solid rgba(125, 211, 252, 0.3);
}

.consistency-overview {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.consistency-overview__card {
  display: grid;
  gap: 8px;
  padding: 18px 20px;
  border-radius: 22px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(248, 250, 252, 0.98));
  border: 1px solid rgba(148, 163, 184, 0.16);
}

.consistency-overview__card span,
.consistency-overview__card small {
  color: #64748b;
}

.consistency-overview__card strong {
  color: #0f172a;
  font-size: 28px;
}

.consistency-grid {
  display: grid;
  gap: 16px;
}

.consistency-card {
  display: grid;
  gap: 16px;
  padding: 22px;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.98));
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 18px 34px rgba(15, 23, 42, 0.05);
}

.consistency-card__header,
.consistency-card__meta,
.consistency-card__actions,
.result-actions {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.consistency-card__eyebrow {
  display: block;
  color: #0f766e;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.consistency-card__header strong,
.consistency-card__preview-placeholder strong {
  color: #0f172a;
}

.consistency-card__meta {
  color: #64748b;
  font-size: 13px;
}

.consistency-card__body {
  display: grid;
  grid-template-columns: 280px minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}

.consistency-card__preview {
  align-self: start;
  aspect-ratio: 4 / 5;
  border-radius: 22px;
  overflow: hidden;
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
  border: 1px solid rgba(148, 163, 184, 0.16);
}

.consistency-card__preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  background: #f8fafc;
}

.consistency-card__preview-placeholder {
  min-height: 220px;
  display: grid;
  place-items: center;
  padding: 20px;
  text-align: center;
  color: #e2e8f0;
}

.consistency-card__preview-placeholder p {
  margin: 8px 0 0;
  color: #cbd5e1;
  line-height: 1.7;
}

.consistency-card__fields {
  display: grid;
  gap: 12px;
}

.consistency-card__field-hint {
  margin: -4px 0 4px;
  color: #64748b;
  font-size: 12px;
  line-height: 1.7;
}

.consistency-card__actions {
  justify-content: flex-start;
}

.consistency-card__actions :deep(.el-button) {
  min-height: 44px;
  border-radius: 14px;
}

.result-actions {
  justify-content: flex-end;
}

@media (max-width: 960px) {
  .consistency-workbench__header,
  .result-actions {
    flex-direction: column;
    align-items: stretch;
  }

  .workspace-switcher {
    width: 100%;
    justify-content: space-between;
  }

  .consistency-overview,
  .consistency-card__body {
    grid-template-columns: 1fr;
  }
}
</style>